import { localToWorld, obbCorners, rotate, add, type Obb2, type Vec2 } from '@indusketch/geometry';
import type { Clearance, PlacedObject, ProjectDocument } from './types.js';

const visibleCache = new WeakMap<ProjectDocument, ProjectDocument>();

/**
 * The document without hidden objects, for everything the user sees and measures against
 * (plan, 3D view, collisions, snapping, distances). Cached per document instance.
 */
export function visibleDocument(doc: ProjectDocument): ProjectDocument {
  let out = visibleCache.get(doc);
  if (!out) {
    const objects = Object.values(doc.objects);
    out = objects.some((o) => o.hidden)
      ? { ...doc, objects: Object.fromEntries(objects.filter((o) => !o.hidden).map((o) => [o.id, o])) }
      : doc;
    visibleCache.set(doc, out);
  }
  return out;
}

/**
 * Local object frame: width along x, depth along y, origin at the footprint center.
 * "Front" is the −y side, i.e. facing south / the bottom of the plan at rotation 0.
 */
export function objectObb(o: Pick<PlacedObject, 'transform' | 'dimensions'>): Obb2 {
  return {
    center: { x: o.transform.position.x, y: o.transform.position.y },
    width: o.dimensions.width,
    depth: o.dimensions.depth,
    rotation: o.transform.rotation,
  };
}

/** Footprint enlarged by the (possibly asymmetric) clearance, or null if none. */
export function clearanceObb(
  o: Pick<PlacedObject, 'transform' | 'dimensions' | 'clearance'>,
): Obb2 | null {
  const c: Clearance | null = o.clearance;
  if (!c || c.front + c.back + c.left + c.right <= 0) return null;
  const shiftLocal = { x: (c.right - c.left) / 2, y: (c.back - c.front) / 2 };
  const center = add(
    { x: o.transform.position.x, y: o.transform.position.y },
    rotate(shiftLocal, o.transform.rotation),
  );
  return {
    center,
    width: o.dimensions.width + c.left + c.right,
    depth: o.dimensions.depth + c.front + c.back,
    rotation: o.transform.rotation,
  };
}

export const objectCorners = (o: Pick<PlacedObject, 'transform' | 'dimensions'>): Vec2[] =>
  obbCorners(objectObb(o));

/** Small triangle on the front edge so orientation is visible in the plan. */
export function objectFrontMarker(o: Pick<PlacedObject, 'transform' | 'dimensions'>): Vec2[] {
  const hw = o.dimensions.width / 2;
  const hd = o.dimensions.depth / 2;
  const s = Math.min(hw, hd, 0.6) * 0.6;
  const placement = {
    position: { x: o.transform.position.x, y: o.transform.position.y },
    rotation: o.transform.rotation,
  };
  return [
    { x: -s, y: -hd + s * 0.2 },
    { x: s, y: -hd + s * 0.2 },
    { x: 0, y: -hd + s * 1.2 },
  ].map((p) => localToWorld(p, placement));
}

/** Scale factors that map the native model size onto the requested real size. */
export function assetScale(target: PlacedObject['dimensions'], native: PlacedObject['dimensions']) {
  return {
    x: target.width / native.width,
    y: target.depth / native.depth,
    z: target.height / native.height,
  };
}
